import React from 'react';
import _, { mapValues } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import useApp from 'new/state/app/repository/useApp';
import { useDataMaps } from 'new/hooks/adapters/useDataMaps';
import ConfigureTemplatizedAPI from 'js/components/data_sources/add_ds_form/ConfigureTemplatizedAPI';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import { useRestAPIContext } from 'new/components/flows/configure/RestAPISource/RestAPISourceProvider';
import useProbeAdapter from 'new/state/probe/adapter/useProbeAdapter';
import useCredentialsAdapter from 'new/state/credentials/adapter/useCredentialsAdapter';
import {
  markAsPaginatedIterationIfApplicable,
  resolveURLTemplate,
} from 'new/components/flows/configure/RestAPISource/RestAPISource.utils';

export function RestTemplatizedAPI({ showExternalTrigger }: { showExternalTrigger?: boolean }) {
  const ctx = useApp();
  const [state, actions] = useRestAPIContext();
  const { vendorEndpoints } = useVendors();
  const { getDataCredential } = useCredentialsAdapter();
  const { getProbeSample } = useProbeAdapter();
  const { dataMaps, dataMapSamples, fetchDataMaps } = useDataMaps();
  const { updateFormData, data } = useFlowsSetup(state.formType, state.flowType);

  const {
    paging,
    apiConfig,
    sourceConfig,
    visibleFields,
    advancedFields,
    selectedConnection,
    selectedCredential,
    templatizedAPIConfig,
    apiResponseByStage,
    apiResponseUpdateTime,
    isLoadingProbeSample,
    dataPathValidationResultByStage,
  } = state;

  const credID = selectedCredential?.id;
  const selectedConnectionDetails = state.meta;

  const handleChangeDataSourceDetails = ({ name, property, value }) => {
    updateFormData({
      sourceConfig: {
        ...sourceConfig,
        [name || property]: value,
      },
    });
  };

  const onTemplatizedAPIConfigChanged = (name: string, value: any, _ignore: any, data: any) => {
    const sourceConfigById = paging.sourceConfigById;
    let newTemplatizedAPIConfig = {
      ...templatizedAPIConfig,
      ...data,
      [name]: value,
    };

    if (name === 'vendor_endpoint_id' && templatizedAPIConfig.vendor_endpoint_id !== value) {
      newTemplatizedAPIConfig = UIUtils.configWithMissingDefaults(
        newTemplatizedAPIConfig,
        sourceConfigById[selectedConnection],
      );
    }

    const newFormData = {
      apiConfig,
      templatizedAPIConfig: newTemplatizedAPIConfig,
    };

    // derive source_config from template_config to keep Advanced Mode in sync
    const { vendor_endpoint_id: endpointId } = newTemplatizedAPIConfig;

    if (
      endpointId &&
      vendorEndpoints[endpointId] &&
      !_.isEmpty(vendorEndpoints[endpointId].source_template)
    ) {
      const template = vendorEndpoints[endpointId].source_template;
      const fields = sourceConfigById[selectedConnection];
      const newAPIConfig = {
        ...apiConfig,
        'rest.iterations': _.map(template['rest.iterations'], (iteration) =>
          mapValues(iteration, (value, key) =>
            resolveURLTemplate({
              key,
              value,
              fields,
              selectedCredential,
              config: newTemplatizedAPIConfig,
            }),
          ),
        ),
      } as any;

      /**
       * backward compatibility:
       * - when switching to advanced view
       * - lets just add a isPaginated=== true property when using valid paginated iterations.
       */
      const { advanced_templatized_api } = newAPIConfig;
      if (advanced_templatized_api === true) {
        const iterations = newAPIConfig['rest.iterations'];
        newFormData.apiConfig = {
          ...newAPIConfig,
          'rest.iterations': _.map(iterations, markAsPaginatedIterationIfApplicable),
        };
      } else {
        newFormData.apiConfig = newAPIConfig;
      }
    }
    updateFormData(newFormData);
  };

  return (
    <ConfigureTemplatizedAPI
      paging={paging}
      dataMaps={dataMaps}
      apiConfig={apiConfig}
      sourceConfig={sourceConfig}
      flowType={state.flowType}
      selectedCredentialId={credID}
      dataMapSamples={dataMapSamples}
      vendorEndpoints={vendorEndpoints}
      sourceName={apiConfig?.sourceName}
      sourceDescription={apiConfig?.sourceDescription}
      apiResponse={apiResponseByStage?.[0]}
      advancedFields={advancedFields}
      visibleFieldData={visibleFields}
      apiResponseByStage={apiResponseByStage}
      selectedCredential={selectedCredential}
      selectedConnection={selectedConnection}
      isLoadingProbeSample={isLoadingProbeSample}
      templatizedAPIConfig={templatizedAPIConfig}
      apiResponseUpdateTime={apiResponseUpdateTime}
      selectedConnectionDetails={selectedConnectionDetails}
      dataPathValidationResult={dataPathValidationResultByStage?.[0]}
      dataPathValidationResultByStage={dataPathValidationResultByStage}
      sourceBeingEdited={data?.source}
      showExternalTrigger={showExternalTrigger}
      // actions
      loadDataMaps={fetchDataMaps}
      onAddStep={actions.onAddStep}
      probeGetSample={getProbeSample}
      setFormError={actions.showError}
      onRemoveStep={actions.onRemoveStep}
      getDataCredential={getDataCredential}
      onCallRESTAPI={actions.onProbeGetSample}
      onCallAPI={actions.onProbeTemplatizedAPI}
      onInputChange={handleChangeDataSourceDetails}
      validateDataPath={actions.onValidateDataPath}
      onClearAPIResponse={actions.onProbeClearSample}
      onAPIConfigChanged={actions.onAPIConfigChanged}
      onSourceConfigChanged={actions.onSourceConfigChanged}
      onGetParameterizedURLs={actions.onGetParameterizedURLs}
      onTemplatizedAPIConfigChanged={onTemplatizedAPIConfigChanged}
      setContextualSupport={ctx.setContextualSupport}
      unsetContextualSupport={ctx.unsetContextualSupport}
      onSaveAdvancedMode={actions?.onSaveAdvancedMode}
      nextFormStage={actions?.nextFormStage}
      goBack={actions?.goBack}
      onError={actions?.onError}
    />
  );
}
